import { motion } from "motion/react";
import type React from "react";
import { useCallback, useState } from "react";
import { Link, useLocation } from "wouter";
import { Input } from "@/components/ui/input.tsx";
import { cn } from "../../lib/utils.ts";
import type { UserResponseArtifact } from "../../types/artifacts.ts";
import type { GetThreadResp } from "../../types/thread.ts";
import { breakWordsContainer } from "../layout/constants.ts";
import { InlineMarkdown } from "../markdown/InlineMarkdown.tsx";
import { CardFacepile } from "./CardFacepile.tsx";
import { THREAD_FLASH_ANIMATION, useThreadFlash } from "./threadFlash.ts";
import { ThreadCardFooter } from "./ThreadCardFooter.tsx";
import { ThreadRenameProvider } from "./ThreadItemActions.tsx";
import { inlineRenameFieldClass, useInlineRename } from "./useInlineRename.ts";
import {
  ExternalChannelBadge,
  getThreadItemInteractionStyles,
  InvitationConflictBadge,
  RoutineTriggeredBadge,
  ThreadStatusBadge,
} from "./shared.tsx";
import { formatDateForThreadList, getThreadPagePath, getThreadParticipantsPath } from "./util.ts";

/**
 * Header section of the thread card: single flex row of inline status /
 * routine / conflict chips on the left, date on the right. Archived threads
 * show the archived chip in the same row instead of shuffling layout.
 */
const ThreadCardHeader = ({
  thread,
  isRenaming,
  onRenameDone,
}: {
  thread: GetThreadResp;
  isRenaming: boolean;
  onRenameDone: () => void;
}) => {
  const [, setLocation] = useLocation();
  const participants = thread.card?.participants ?? [];
  const additional = thread.card?.additionalParticipantCount ?? 0;
  const { inputProps } = useInlineRename({
    threadId: thread.id,
    title: thread.title,
    isEditing: isRenaming,
    onDone: onRenameDone,
  });
  // The card body is already a `<Link>`, so the facepile navigates imperatively
  // rather than nesting an anchor — see `ThreadRow` for the same constraint.
  const openParticipants = () => {
    setLocation(getThreadParticipantsPath(thread.id));
  };

  return (
    <div className="flex flex-col -mb-1.5">
      <div className="flex items-center h-6 mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <ThreadStatusBadge thread={thread} />
          <ExternalChannelBadge thread={thread} />
          <InvitationConflictBadge thread={thread} />
          <RoutineTriggeredBadge thread={thread} />
        </div>
        <div className="flex-1" />
        {(participants.length > 0 || additional > 0) && (
          <CardFacepile
            participants={participants}
            additional={additional}
            onClick={openParticipants}
          />
        )}
        {/* No pin control in this header: pinning lives in the footer's overflow
            menu beside Archive, so the card offers its actions in one place
            rather than one in each corner. The list's "Pinned" group marks the
            state. */}
        <div className="text-xs text-muted-foreground shrink-0">
          {formatDateForThreadList(thread.sortDate)}
        </div>
      </div>
      {isRenaming ? (
        // Same line, same weight, same height as the heading it replaces — the
        // card's body sits directly below, and a field that changed the header's
        // height would shove the whole card as the rename opened.
        <Input {...inputProps} className={cn(inlineRenameFieldClass, "w-full font-semibold")} />
      ) : (
        <h3 className="font-semibold truncate">{thread.title || "New thread"}</h3>
      )}
    </div>
  );
};

/**
 * Body section of the thread card showing the thread content.
 *
 * The server emits `thread.card.body` with artifact-aware copy (see A-series
 * sub-issues under BIG-4119) — we render that directly. On the rare fetch
 * paths that don't populate `card` (see `card` comment in `types/thread.ts`),
 * fall back to the first-sentence extraction from `activeMessage.content`.
 */
const ThreadCardBody = ({ thread }: { thread: GetThreadResp }) => {
  if (thread.card) {
    return (
      <div className="flex flex-col flex-1 min-h-0 gap-1">
        <div
          className={cn(
            // max-w-none: drop prose's 65ch line cap — the card's own width is
            // the measure, so wide sidebars don't leave a dead column of space.
            "prose prose-zinc prose-sm max-w-none leading-5",
            // Clamp to exactly 3 lines with an ellipsis.
            "line-clamp-3",
          )}
        >
          <InlineMarkdown allowedElements={["strong", "em", "code"]}>
            {thread.card.body}
          </InlineMarkdown>
        </div>
      </div>
    );
  }

  let content = "";

  const userResponseArtifact = thread.activeMessage?.artifacts?.find(
    (artifact): artifact is UserResponseArtifact => artifact.artifactType === "userResponse",
  );

  if (
    thread.activeMessage?.type === "agentQuestion" &&
    (userResponseArtifact?.form?.fields?.length ?? 0) > 1
  ) {
    content = "I have some things I need you to clarify before I can continue.";
  } else {
    const fullContent = userResponseArtifact?.response || thread.activeMessage?.content || "";
    // Extract first sentence: match up to punctuation followed by space+capital or end of string
    content = fullContent.replace(/^([\s\S]*?[.?!。？！])(?=\s+[A-Z]|$).*/, "$1");
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-1">
      <div className="flex-1 min-h-0 overflow-clip">
        <div
          className={cn(
            // This summary should be tailored to fit in this UI without overflow, but in the worst case,
            // we fall back to a scroll, because the user can't just tap through to see the full thing.
            // max-w-none: drop prose's 65ch line cap — the card's own width is the
            // measure, and the max-height clamp already bounds reading effort.
            "prose prose-zinc prose-sm max-w-none leading-5 overflow-y-auto scrollbar-thin",
            // max lines - em's are a quick and dirty fallback for older browsers
            "max-h-[8em] [@supports(height:1lh)]:max-h-[5.5lh]",
          )}
        >
          <InlineMarkdown allowedElements={["strong", "em", "code"]}>{content}</InlineMarkdown>
        </div>
      </div>
    </div>
  );
};

/**
 * Displays a thread as a card with header, content, and footer sections
 */

export const ThreadCard = ({
  ref,
  thread,
  isSelected,
  onSelect,
}: {
  thread: GetThreadResp;
  isSelected: boolean;
  ref?: React.ComponentPropsWithRef<typeof motion.a>["ref"];
  /**
   * When provided, the card renders the same Link/anchor surface but a
   * plain left-click is intercepted and `onSelect(thread.id)` is called
   * instead of navigating. Right-click / cmd-click / middle-click still
   * open the thread page in a new tab. Mirrors `ThreadRow`'s
   * sidebar-friendly behavior — used inside the calendar's left aside.
   */
  onSelect?: (threadId: number) => void;
}) => {
  const { flash, clearFlash } = useThreadFlash(thread.id);

  const [isRenaming, setIsRenaming] = useState(false);
  const stopRenaming = useCallback(() => setIsRenaming(false), []);
  const startRenaming = useCallback(() => setIsRenaming(true), []);

  // While the title is being edited the card is not a link — clicking into the
  // field or selecting a word in it would otherwise leave for the thread
  // mid-rename. Checked ahead of the modifier keys so cmd-click does not open a
  // second tab on a thread the user is in the middle of naming.
  //
  // Otherwise, when `onSelect` is supplied, intercept the click and call the
  // handler instead of navigating. The <Link> remains so right-click /
  // cmd-click "open in new tab" and keyboard semantics still work — only the
  // default left-click navigation is hijacked. Mirrors ThreadRow.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isRenaming) {
      e.preventDefault();
      return;
    }
    if (!onSelect) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    onSelect(thread.id);
  };

  return (
    <ThreadRenameProvider value={startRenaming}>
      <Link href={getThreadPagePath(thread.id)} asChild onClick={handleClick}>
        {/* biome-ignore lint/a11y/useValidAnchor: href is injected by Link's asChild via cloneElement */}
        <motion.a
          // `position` (not full `layout`): animate reorder/position changes, but
          // never size. A full layout animation scale-projects width changes from
          // the pane resize, which warps the card text and lags the body behind the
          // divider. The card height is fixed, so dropping size animation is lossless.
          layout="position"
          layoutId={`thread-card-${thread.id}`}
          initial={{ pointerEvents: "none" }}
          animate={{ pointerEvents: "auto" }}
          exit={{ pointerEvents: "none" }}
          transition={{
            layout: { duration: 0.3 },
            pointerEvents: { duration: 0 },
          }}
          className={cn(
            "group/thread flex flex-col min-w-0",
            "md:h-[220px] h-[196px] p-3 flex flex-col gap-4 bg-popover text-popover-foreground rounded-2xl",
            // The keyboard-focus ring belongs to the list, not the card: the
            // wrapper `ThreadList` renders around each card draws it, so the
            // card never carries a selection border of its own.
            getThreadItemInteractionStyles(false),
            // Holds the card's hover treatment for the length of the rename, so
            // the card being edited stays the lit one in the grid even after the
            // pointer has left it. Mirrors the row's pinned pill.
            isRenaming && "border-border shadow-lg",
            breakWordsContainer,
            "no-underline",
            flash && "animate-thread-flash",
          )}
          onAnimationEnd={
            flash
              ? (e) => {
                  if (e.animationName === THREAD_FLASH_ANIMATION) clearFlash();
                }
              : undefined
          }
          ref={ref}
          aria-hidden={false}
          tabIndex={0}
          // An `<a href>` is draggable by default, and that wins over selecting
          // text inside it: press-and-drag across the title field started
          // dragging the *link* — ghost image, URL and all — instead of
          // highlighting a word, and dropping it back on the field pasted the
          // thread's URL into the name. Only while renaming; dragging a thread
          // link out to a bookmark bar or another window is worth keeping the
          // rest of the time.
          draggable={isRenaming ? false : undefined}
        >
          <ThreadCardHeader thread={thread} isRenaming={isRenaming} onRenameDone={stopRenaming} />
          <ThreadCardBody thread={thread} />
          <ThreadCardFooter thread={thread} isSelected={isSelected} />
        </motion.a>
      </Link>
    </ThreadRenameProvider>
  );
};
